import { IonLabel, IonSegment, IonSegmentButton } from '@ionic/react';
import { ComponentProps } from 'react';
import { FormattedMessage } from 'react-intl';

import styles from '@components/buttons/Segment.module.css';
import Nbsp from '@components/Nbsp';
import Icon from '@components/text/Icon';
import { IconName } from '@utils/iconUtils';
import { clickCurrentTarget } from '@utils/keyboardUtils';

type SegmentButtonProps = Pick<
  ComponentProps<typeof IonSegmentButton>,
  'disabled' | 'onClick' | 'onKeyUp' | 'ref' | 'tabIndex' | 'type' | 'value'
>;

export type Icon =
  | {
      color: string;
      name: IconName;
    }
  | undefined;

export type ButtonProps<T> = (SegmentButtonProps & {
  disabled?: boolean;
  icon?: Icon;
  maxWidth: number;
  textKey: string;
  value: T;
})[];

type Props<T> = {
  buttons: ButtonProps<T>;
  count?: number;
  fullWidth?: boolean;
  onChange: (t: T) => void;
  skipSpacing?: boolean;
  value: T;
};

const Segment = <T extends string>({ buttons, count, fullWidth, onChange, skipSpacing = false, value }: Props<T>) => (
  <div className={`${styles.segmentContainer} ${fullWidth ? styles.fullWidth : ''}`}>
    <IonSegment
      className={`${styles.segment} ${skipSpacing ? styles.skipSpacing : ''}`}
      mode="ios"
      onIonChange={e => onChange(e.detail.value as T)}
      value={value}
    >
      {buttons.map(button => (
        <IonSegmentButton
          className={`${styles.contentSegment} ${button.disabled ? styles.disabled : ''}`}
          disabled={button.disabled}
          key={button.textKey}
          onKeyUp={clickCurrentTarget}
          style={{ maxWidth: button.maxWidth }}
          value={button.value}
        >
          <IonLabel>
            <FormattedMessage id={button.textKey} />
            {count !== undefined && button.value !== value && (
              <>
                <Nbsp />
                <FormattedMessage id="dictionary.count" values={{ count }} />
              </>
            )}
            {button.icon && (
              <Icon
                ariaLabelKey={button.textKey}
                className={`${styles[button.icon.color]} ${styles.icon}`}
                icon={button.icon.name}
              />
            )}
          </IonLabel>
        </IonSegmentButton>
      ))}
    </IonSegment>
  </div>
);

export default Segment;
